import { api } from '@api'
import { useGetArtist } from '@hooks/TRPC_Hooks/useGetArtist'
import { useGetBonusCollection } from '@hooks/TRPC_Hooks/useGetBonusCollection'
import { useGetCollectedToyInstances } from '@hooks/TRPC_Hooks/useGetCollectedToyInstances'
import { useGetCollectionEarningsCount } from '@hooks/TRPC_Hooks/useGetCollectionEarningsCount'
import { useGetSingleCollection } from '@hooks/TRPC_Hooks/useGetSingleCollection'
import { useGetStoreItems } from '@hooks/TRPC_Hooks/useGetStoreItems'
import { useGetToy } from '@hooks/TRPC_Hooks/useGetToy'
import { useIsCollectionBonusToyCollected } from '@hooks/TRPC_Hooks/useIsCollectionBonusToyCollected'
import { useProfile } from '@hooks/TRPC_Hooks/useProfile'
import { useToyActiveDrops } from '@hooks/TRPC_Hooks/useToyActiveDrops'
import { DropMechanics } from '@specs/specs'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { getArtistWithCollaboratorsId, haveAvailableToys } from '@specs/helpers'
import { useGetBonusToy } from '@hooks/TRPC_Hooks/useGetBonusToy'
import { PageQueries } from '@specs/navigation'
import { useGetUserStoreItems } from './TRPC_Hooks/useGetUserStoreItems'

interface getToyDataProps {
    toyId: number
}

export const useToyData = ({ toyId }: getToyDataProps) => {
    const router = useRouter()
    const { status } = useSession()
    const { secret, priceSortCurrency }: PageQueries = router.query

    const { data: profile, isLoading: isProfileLoading } = useProfile()
    const { data: toy, isLoading: toyLoading } = useGetToy({ id: toyId })
    const { data: toyInstances, isLoading: isInstancesLoading } = useGetCollectedToyInstances({ toyId })
    const { data: drops, isLoading: isDropsLoading } = useToyActiveDrops({ toyId })
    const { data: bonusCollection, isLoading: bonusCollectionLoading } = useGetBonusCollection({ toyid: toyId })
    const { data: stores, isLoading: isStoreLoading } = useGetStoreItems({ limit: 1, toyId, currency: priceSortCurrency })
    const { data: myStores, isLoading: isMyStoresLoading } = useGetUserStoreItems({ toyId, currency: priceSortCurrency })

    const collectionId = toy?.asCollectionBonus?.[0]?.collectionid || toy?.collections?.[0]?.collectionid
    const artistId = toy?.authorid

    const IsEnabledCollectionDataFetch = typeof collectionId === 'number'

    const { data: collection, isLoading: collectionLoading } = useGetSingleCollection(
        { id: collectionId ? collectionId : bonusCollection?.collectionid ? bonusCollection.collectionid : -1 },
        {
            enabled: IsEnabledCollectionDataFetch,
        },
    )
    const { data: bonusToy, isLoading: bonusToyLoading } = useGetBonusToy(
        { id: collectionId ?? -1 },
        {
            enabled: IsEnabledCollectionDataFetch,
        },
    )

    const isBonusToy = bonusToy?.toyid === toyId

    const { data: artist, isLoading: artistLoading } = useGetArtist(
        { id: artistId ?? -1 },
        {
            enabled: typeof artistId === 'number',
        },
    )

    const artistWithCollaboratorsIds = getArtistWithCollaboratorsId(
        artist?.profile?.shortid,
        toy?.collaborators?.map(({ profile }) => profile?.shortid),
    )

    const { data: collected, isLoading: collectedLoading } = useIsCollectionBonusToyCollected(
        { toyId },
        {
            enabled: isBonusToy,
        },
    )
    const { data: earned, isLoading: earnedLoading } = useGetCollectionEarningsCount(
        { collectionId: collectionId ?? -1 },
        {
            enabled: !!collection,
        },
    )

    const { data: collectionMeta, isLoading: collectionMetaLoading } = api.ToyManager.getCollectionShortMeta.useQuery(
        { slug: collection?.slug ?? '' },
        {
            queryKey: ['ToyManager.getCollectionShortMeta', { slug: collection?.slug ?? '' }],
            refetchOnWindowFocus: false,
            staleTime: 60 * 60 * 1000, // 1 hour
            enabled: !!collection,
        },
    )

    const currentProfileId = useMemo(() => profile?.id, [profile])
    const isSecret = typeof secret === 'string'

    // TODO: Костыль. Поменять на нормальную механику обработки всех типов дропов.
    const [_drop] = drops?.filter(({ mechanic }) => mechanic !== DropMechanics.SECRET_PERSONAL && mechanic !== DropMechanics.SECRET) ?? []
    const currentDrop = isSecret ? drops?.find(({ mechanic }) => mechanic === DropMechanics.SECRET_PERSONAL || mechanic === DropMechanics.SECRET) : _drop

    const dropMechanic = currentDrop ? currentDrop?.mechanic : undefined

    const isCollected = isBonusToy
        ? !!collected
        : !!toyInstances && (toyInstances.dropOwnerships.some(({ profileid }) => profileid === currentProfileId) || toyInstances.ownerships.some(({ profileid }) => profileid === currentProfileId))

    const isArtClub = !!toy?.badges?.some((badge) => badge === 'artClub') || dropMechanic === DropMechanics.ARTCLUB
    const isArtClubMember = !!profile?.isArtClubMember

    const isCollectableSecretDrop =
        (dropMechanic === DropMechanics.SECRET || dropMechanic === DropMechanics.SECRET_PERSONAL) && (isSecret || !currentDrop?.earnings?.some(({ profileid }) => profileid === currentProfileId))
    const isCollectableDefaultDrop =
        typeof currentDrop !== 'undefined' && !currentDrop.earnings?.some(({ profileid }) => profileid === currentProfileId) && !(dropMechanic === DropMechanics.SECRET || dropMechanic === DropMechanics.SECRET_PERSONAL)

    const isCollectableBonusToy = typeof earned === 'number' && typeof collectionMeta?.toysTotal === 'number' ? earned === collectionMeta.toysTotal : false
    const isCollectablePartnerGift = !!toy?.badges?.some((badge) => badge === 'digitalTwin')

    const isCollectable = !isCollected && haveAvailableToys(toy) && (isCollectableSecretDrop || isCollectableDefaultDrop || isCollectableBonusToy || isCollectablePartnerGift)

    const isLoading = useMemo(
        () =>
            toyLoading ||
            bonusCollectionLoading ||
            (status === 'authenticated' && (isInstancesLoading || isDropsLoading || isStoreLoading || isProfileLoading)) ||
            (status === 'authenticated' && isMyStoresLoading) ||
            (status === 'authenticated' && !!collection && earnedLoading) ||
            (status === 'authenticated' && isBonusToy && collectedLoading) ||
            (IsEnabledCollectionDataFetch && (collectionLoading || bonusToyLoading)) ||
            (!!artistId && artistLoading) ||
            (!!collection && collectionMetaLoading),
        [
            status,
            toyLoading,
            bonusCollectionLoading,
            isInstancesLoading,
            isDropsLoading,
            isStoreLoading,
            isMyStoresLoading,
            isProfileLoading,
            isMyStoresLoading,
            collection,
            earnedLoading,
            isBonusToy,
            collectedLoading,
            IsEnabledCollectionDataFetch,
            collectionLoading,
            artistId,
            artistLoading,
            collectionMetaLoading,
            bonusToyLoading,
        ],
    )

    const firstStoreItem = useMemo(() => stores?.items?.[0], [stores])
    const myFirstStoreItem = useMemo(() => myStores?.[0], [myStores])
    const haveFigurinesForSale = useMemo(() => isCollected && (toyInstances?.ownerships?.length ?? 0) > (myStores?.length ?? 0), [isCollected, toyInstances, myStores])
    const soldOut = currentDrop && dropMechanic === 'BUY' && toy?.available === 0

    return {
        isCollected,
        haveFigurinesForSale,
        firstStoreItem,
        myFirstStoreItem,
        collection,
        currentDrop,
        artist,
        isArtClub,
        isCollectable,
        isArtClubMember,
        isSecret,
        dropMechanic,
        isLoading,
        toy,
        isBonusToy,
        artistWithCollaboratorsIds,
        isCollectableDefaultDrop,
        soldOut,
        earnedFromCollectionCount: earned,
    }
}
